import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getDday } from '@utils/dateUtils';
import { AppPopupType } from '@src/constants/adminRegisteredPopup';

/**
 * 어드민에서 등록한 팝업 리스트
 * 기본적으로 하루에 한번 노출(날짜 기준), 다시보지 않기 선택 가능
 * */
interface AdminRegisteredPopupData {
  appPopupId: string;
  popupShowPage: 'HOME' | 'MY_RANKING';
  popupCheckedDate?: string;
  neverShow: boolean;
}
export const useAdminRegisteredPopupStore = create(
  persist<{
    list: AdminRegisteredPopupData[];
    updateList: (popupList: AppPopupType[]) => void;
    getItem: (appPopupId: string) => AdminRegisteredPopupData | undefined;
    updateItemCheckedDateToToday: (appPopupId: string) => void;
    updateItemNeverShow: (appPopupId: string, value: boolean) => void;
    shouldShowPopupToday: (appPopupId: string) => boolean;
  }>(
    (set, get) => ({
      list: [],
      updateList: (popupList: AppPopupType[]) =>
        // api에서 받은 팝업만 남도록 업데이트
        set((prev) => ({
          ...prev,
          list: popupList.map((item) => ({
            appPopupId: item.appPopupId,
            popupShowPage: item.popupShowPage,
            popupCheckedDate:
              get().getItem(item.appPopupId)?.popupCheckedDate || undefined,
            neverShow: !!get().getItem(item.appPopupId)?.neverShow,
          })),
        })),
      getItem: (appPopupId: string) =>
        get().list.find((popup) => appPopupId === popup.appPopupId),
      updateItemCheckedDateToToday: (appPopupId: string) =>
        set((prev) => ({
          ...prev,
          list: prev.list.map((popup) => {
            if (appPopupId === popup.appPopupId) {
              const today = new Date().toISOString();
              return {
                ...popup,
                popupCheckedDate: today,
              };
            }
            return popup;
          }),
        })),
      updateItemNeverShow: (appPopupId: string, value: boolean) =>
        set((prev) => ({
          ...prev,
          list: prev.list.map((popup) => {
            if (appPopupId === popup.appPopupId) {
              return {
                ...popup,
                neverShow: value,
              };
            }
            return popup;
          }),
        })),
      shouldShowPopupToday: (appPopupId: string) => {
        const popupCheckedDate = get().getItem(appPopupId)?.popupCheckedDate;
        const neverShow = get().getItem(appPopupId)?.neverShow;

        if (neverShow) {
          return false;
        }
        if (!popupCheckedDate) {
          return true;
        } else {
          // 오늘 봤으면 false, 이전에 봤으면 true
          const dayPassed = getDday(popupCheckedDate);
          return dayPassed < 0;
        }
      },
    }),
    {
      name: 'adminRegisteredPopup',
    },
  ),
);

/**
 * 홈 화면 알림허용 바텀시트
 * 수동 로그인 시 알림이 OFF 되어있는 경우에 노출, "COUPON 강조 타입" / "RANKING 강조 타입"이 번갈아가며 표시됨
 * */
export const usePushAgreePopupStore = create(
  persist<{
    hasShownPushAgreePopup: boolean;
    setHasShownPushAgreePopup: (isAgree: boolean) => void;
    lastShowedPopupUiType: 'COUPON' | 'RANKING';
    setLastShowedPopupUiType: (type: 'COUPON' | 'RANKING') => void;
  }>(
    (set) => ({
      hasShownPushAgreePopup: false,
      setHasShownPushAgreePopup: (value: boolean) =>
        set((prev) => ({
          ...prev,
          hasShownPushAgreePopup: value,
        })),
      lastShowedPopupUiType: 'RANKING',
      setLastShowedPopupUiType: (type: 'COUPON' | 'RANKING') =>
        set((prev) => ({
          ...prev,
          lastShowedPopupUiType: type,
        })),
    }),
    {
      name: 'pushAgreePopup',
    },
  ),
);

/* 해피아워 쿠폰 홍보 팝업 */
export const usePromotionPopupStore = create(
  persist<{
    isPopupOpen: boolean;
    setIsPopupOpen: (value: boolean) => void;
  }>(
    (set) => ({
      isPopupOpen: true,
      setIsPopupOpen: (value) =>
        set({
          isPopupOpen: value,
        }),
    }),
    {
      name: 'promotionPopup',
    },
  ),
);

/**
 * MY랭킹 SNS 연결 팝업
 * 다시보지 않기 처리 시 30일동안 보지 않음
 * */
export const useSnsRemindPopupCheckedDateStore = create(
  persist<{
    pushPopupCheckedDate?: string;
    setPopupCheckedDate: (value: string) => void;
    neverShow: boolean;
    setNeverShow: (value: boolean) => void;
    shouldShowPopupToday: () => boolean;
  }>(
    (set, get) => ({
      pushPopupCheckedDate: undefined,
      setPopupCheckedDate: (value: string) =>
        set((prev) => ({
          ...prev,
          pushPopupCheckedDate: value,
        })),
      neverShow: false,
      setNeverShow: (value: boolean) =>
        set((prev) => ({
          ...prev,
          neverShow: value,
        })),
      shouldShowPopupToday: () => {
        const popupCheckedDate = get().pushPopupCheckedDate;
        const neverShow = get().neverShow;

        if (!popupCheckedDate) {
          return true;
        } else {
          const dayPassed = getDday(popupCheckedDate);
          if (neverShow && dayPassed >= -30) {
            // 한달동안 보지 않음
            return false;
          }
          return dayPassed < 0;
        }
      },
    }),
    {
      name: 'snsPopupRemindCheckedDate',
    },
  ),
);

/**
 * MY랭킹 -> 내 포토카드 상세 페이지 최초 진입 시 "순위"에 대한 안내 툴팁 표시용
 * */
export const useRankInfoTooltipStore = create(
  persist<{
    isDisplayedOnce: boolean;
    setDisplayedOnce: () => void;
  }>(
    (set) => ({
      isDisplayedOnce: false,
      setDisplayedOnce: () =>
        set({
          isDisplayedOnce: true,
        }),
    }),
    {
      name: 'myPhotoCardRankInfoTooltip', // localStorage
    },
  ),
);

/**
 * 포토카드 등록 완료 시 알림이 OFF 된 경우 노출
 * 다시보지 않기 처리 시 3주동안 보지 않음
 * */
export const useApplyCompletePushPopupCheckedDateStore = create(
  persist<{
    pushPopupCheckedDate: string | undefined;
    setPushPopupCheckedDate: (value: string | undefined) => void;
  }>(
    (set) => ({
      pushPopupCheckedDate: undefined,
      setPushPopupCheckedDate: (value: string | undefined) =>
        set({
          pushPopupCheckedDate: value,
        }),
    }),
    {
      name: 'applyCompletePushPopupCheckedDate',
    },
  ),
);

interface LoginPopupErrorType {
  message: string;
  setMessage: (message: string) => void;
}

export const useErrorStore = create<LoginPopupErrorType>((set) => ({
  message: '',
  setMessage: (message) =>
    set({
      message,
    }),
}));
